// First-run tour step machine. Pure logic + localStorage persistence,
// kept free of React so every transition is unit-testable in node.
//
// The tour is three beats long:
//   edit-number  — a numeric literal in the buffer pulses; the user is
//                  asked to change it while the demo plays.
//   heard-change — the edit landed on a cycle boundary; name the magic
//                  ("that's live-coding") and offer the next beat.
//   meet-agent   — point at the ChatPanel with a one-click prompt.
//
// `done` is terminal. Dismissing at any step jumps straight to it —
// the tour must never trap the user. Persistence is a single flag:
// once onboarded (completed OR dismissed), the tour never re-fires.

export type TourStep =
  | 'idle'
  | 'edit-number'
  | 'heard-change'
  | 'meet-agent'
  | 'done';

export type TourEvent =
  /** The user clicked the first-run "Jump in" CTA. */
  | 'begin'
  /** The editor buffer diverged from the demo while audio was playing. */
  | 'edited-while-playing'
  /** The user acknowledged the heard-change beat. */
  | 'continue'
  /** The user fired the suggested agent prompt. */
  | 'complete'
  /** ✕ / Escape at any step. */
  | 'dismiss';

export function nextStep(step: TourStep, event: TourEvent): TourStep {
  if (event === 'dismiss') return 'done';
  switch (step) {
    case 'idle':
      return event === 'begin' ? 'edit-number' : step;
    case 'edit-number':
      return event === 'edited-while-playing' ? 'heard-change' : step;
    case 'heard-change':
      return event === 'continue' ? 'meet-agent' : step;
    case 'meet-agent':
      return event === 'complete' ? 'done' : step;
    case 'done':
      return 'done';
  }
}

// Versioned so a future tour redesign can re-fire for everyone by
// bumping to :v2 without migrating old values.
export const ONBOARDED_KEY = 'strudel-harness:onboarded:v1';

export function isOnboarded(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(ONBOARDED_KEY) === '1';
  } catch {
    // localStorage may throw in private mode / quota. Treat as
    // onboarded — a broken-storage session would otherwise re-run
    // the tour on every visit.
    return true;
  }
}

export function markOnboarded(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ONBOARDED_KEY, '1');
  } catch {
    // Persistence is best-effort.
  }
}
